/**
 * useAutoLoadSelectedBacktest
 *
 * Keeps the loaded chart/result (`currentResult` in useBacktestStore) in sync
 * with `selectedBacktestId` — which changes from a direct link, an Explore
 * Runs pick, OR a browser back/forward (popstate) navigation handled by
 * useUrlSync.
 *
 * SHA-40: previously the equivalent effect in App.tsx only fired when
 * `!currentResult`, so pressing Back after opening a run via Explore Runs left
 * the *previous* run's stale chart on screen — useUrlSync's popstate handler
 * updated `selectedBacktestId`, but nothing reloaded/cleared the chart to
 * match, so Back appeared to just "reload the chart screen" instead of
 * restoring the prior list/selection.
 *
 * Fix: reload whenever the id no longer matches the loaded result, and clear
 * the chart back to the empty/list state when the id goes back to null (e.g.
 * Back to a bare /backtesting URL with no run selected).
 */

import { useEffect } from 'react';
import { useBacktestStore } from '../stores/backtestStore';
import type { ApiBacktestResult, AggregateBacktestResult } from '../types';

export interface UseAutoLoadSelectedBacktestDeps {
  selectedBacktestId: string | null;
  currentResult: ApiBacktestResult | AggregateBacktestResult | null;
  handleSelectRun: (id: string) => void | Promise<unknown>;
}

export function useAutoLoadSelectedBacktest({
  selectedBacktestId,
  currentResult,
  handleSelectRun,
}: UseAutoLoadSelectedBacktestDeps): void {
  useEffect(() => {
    if (!selectedBacktestId) {
      if (currentResult) {
        useBacktestStore.getState().clear();
      }
      return;
    }
    if (currentResult?.id !== selectedBacktestId) {
      void handleSelectRun(selectedBacktestId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBacktestId]);
}
